<template>
  <div class="chart-providers-overview searchable-table p-4" :class="{ 'chart-providers-overview--social-mode': socialMode }">
    <h4>{{ chartRoute.title }}</h4>
    <p>The leaked files come from 14 offshore service providers that help clients establish companies in secrecy jurisdictions.</p>
    <b-input-group class="mb-4" size="sm">
      <b-input-group-prepend is-text>
        <fa icon="filter" />
      </b-input-group-prepend>
      <b-form-input v-model="filter" type="search" placeholder="Filter providers by name, office location or jurisdiction"/>
    </b-input-group>
    <b-table
      :fields="providerFields"
      :dark="socialMode"
      :items="items"
      class="m-0"
      details-td-class="chart-providers-overview__table__provider__offices"
      responsive
      small
      table-class="chart-providers-overview__table small"
      tbody-tr-class="chart-providers-overview__table__provider"
      thead-tr-class="chart-providers-overview__table__header">
      <template #cell(fullName)="{ item: { name, fullName, offices, link } }">
        <a :href="link">
          <span v-if="offices" class="font-weight-bold">
            <span v-html="highlight(name)"></span>
          </span>
          <span v-else class="font-weight-bold">
            <span v-html="highlight(fullName)"></span>
          </span>
        </a>
      </template>
      <template #cell(timePeriod)="{ item: { timePeriod } }">
        <range-mustache
          :min="1970"
          :max="2021"
          :start="timePeriod | timePeriodStart"
          :end="timePeriod | timePeriodEnd"
          class="text-muted" />
      </template>
      <template #cell(records)="{ item: { records }  }">
        <div class="text-success text-right">
          {{ records | formatNumber }}
        </div>
      </template>
      <template #cell(foundingOfficeLocation)="{ item: { foundingOfficeLocation, offices }, toggleDetails }">
        <span v-html="highlight(foundingOfficeLocation)"></span>
        <b-badge href="#" variant="dark" @click="toggleDetails" v-if="!foundingOfficeLocation">
          <fa icon="caret-down" /> {{ offices.length }} offices
        </b-badge>
      </template>
      <template #cell(jurisdictions)="{ item: { jurisdictions : jurisdictions } }">
        <active-text-truncate :ppms="0.05">
          <span v-html="highlight(jurisdictions)"></span>
        </active-text-truncate>
      </template>
      <template #row-details="{ item: { offices } }">
        <div class="mx-2">
          <b-table
            :fields="officeFields"
            :items="offices"
            caption-top
            small
            table-class="chart-providers-overview__table__provider__offices__table"
            thead-class="border-top d-none">
            <template #table-colgroup>
              <col />
              <col class="--founding-office-location" />
              <col class="--year-founded" />
            </template>
            <template #table-caption>Offices</template>
            <template #cell(fullName)="{ item: { fullName } }">
              <span v-html="highlight(fullName)"></span>
            </template>
            <template #cell(foundingOfficeLocation)="{ item: { foundingOfficeLocation } }">
              <span v-html="highlight(foundingOfficeLocation)"></span>
            </template>
          </b-table>
        </div>
      </template>
    </b-table>
    <p class="text-muted mt-2 small text-right">Source: Pandora Papers data</p>
  </div>
</template>

<script>
import * as d3 from 'd3'
import Fuse from 'fuse.js'
import { camelCase, escapeRegExp, mapKeys, groupBy, reduce, sortBy, trim } from 'lodash'
import RangeMustache from '@/components/RangeMustache'
import chart from '../mixins/chart'

export default {
  mixins: [chart],
  name: 'ChartProvidersOverview',
  components: { RangeMustache },
  filters: {
    cleanList (list) {
      return list.split(', ').map(trim).sort().join(', ')
    },
    formatNumber: d3.format(','),
    timePeriodEnd (timePeriod) {
      return Number(timePeriod.split('-').pop())
    },
    timePeriodStart (timePeriod) {
      return Number(timePeriod.split('-').shift())
    }
  },
  data () {
    return {
      filter: ''
    }
  },
  methods: {
    highlight (text) {
      if (this.filter && text) {
        const regex = new RegExp('(' + escapeRegExp(this.filter) + ')', 'gi')
        return text.replace(regex, '<mark class="p-0">$1</mark>')
      }
      return text
    }
  },
  computed: {
    tsv () {
      return sortBy(this.$options.tsv, 'name').map(row => {
        return mapKeys(row, (_, k) => camelCase(k))
      })
    },
    fuse () {
      return new Fuse(this.tsv, {
        includeScore: false,
        includeMatches: false,
        shouldSort: false,
        threshold: 0.1,
        keys: ['name', 'fullName', 'foundingOfficeLocation', 'jurisdictions']
      })
    },
    tsvFiltered () {
      if (this.filter) {
        return this.fuse.search(this.filter).map(match => match.item)
      }
      return this.tsv
    },
    groupedByProviders () {
      return groupBy(this.tsvFiltered, 'name')
    },
    items () {
      return reduce(this.groupedByProviders, (all, offices, key) => {
        if (offices.length > 1) {
          all.push({ ...offices[0], foundingOfficeLocation: null, offices })
        } else {
          all.push({ ...offices[0] })
        }
        return all
      }, [])
    },
    providerFields () {
      return [
        { key: 'fullName', sortable: true, label: 'Provider', thClass: '--full-name' },
        { key: 'records', sortable: true, thClass: '--records' },
        { key: 'timePeriod', sortable: true, thClass: '--time-period' },
        { key: 'foundingOfficeLocation', thClass: '--founding-office-location' },
        { key: 'yearFounded', thClass: '--year-founded', sortable: true },
        { key: 'jurisdictions', thClass: '--jurisdictions' }
      ]
    },
    officeFields () {
      return [
        { key: 'fullName', label: 'Name' },
        { key: 'foundingOfficeLocation', thClass: '--founding-office-location' },
        { key: 'yearFounded', thClass: '--year-founded' }
      ]
    }
  }
}
</script>

<style lang="scss">
@import '../variables.scss';

.chart-providers-overview {

  &__table {
    border: 1px solid $table-border-color;

    .chart-providers-overview--social-mode & {
      border: 0;
    }

    &__header {
      background: $primary;
      color: white;

      & > th {
        white-space: nowrap;
        min-width: 100px;

        &.--full-name {
          min-width: 250px;
        }

        &.--records {
          max-width: 100px;
          width: 100px;
        }

        &.--time-period {
          min-width: 150px;
          max-width: 250px;
          width: 250px;
        }

        &.--year-founded {
          width: 150px;
          min-width: 150px;
          max-width: 150px;
        }

        &.--founding-office-location {
          min-width: 200px;
          width: 200px;
        }

        &.--jurisdictions {
          min-width: 400px;
          width: 400px;
        }
      }

    }

    &__provider {

      &.b-table-has-details td {
        border-top: 1px solid $border-color;

        .chart-providers-overview--social-mode & {
          border-top-color: $table-dark-border-color;
        }
      }

      td > a:not(.badge) {
        color: inherit;
      }

      td > .active-text-truncate {
        max-width: calc(400px - #{$spacer * 2});
      }

      &.b-table-has-details,
      &.b-table-details {
        background:  $table-accent-bg;

        .chart-providers-overview--social-mode & {
          background: lighten($table-dark-bg, 5%);
        }
      }

      & &__offices {
        padding: 0;

        &__table {
          background: white;

          thead tr > th.--year-founded,
          col.--year-founded {
            width: calc(150px + 400px - 0.5rem);
            min-width: calc(150px + 400px - 0.5rem);
          }

          thead tr > th.--founding-office-location,
          col.--founding-office-location {
            width: 200px;
            min-width: 200px;
          }
        }
      }
    }
  }
}
</style>

<tsv>
name	fullName	records	time_period	year_founded	founding_office_location	jurisdictions	link
AABOL	All About Offshore Limited	270328	2002-2019	2007	Seychelles	Seychelles	https://icij.org/investigations/pandora-papers/
Alcogal	Alemán, Cordero, Galindo & Lee	2185783	1970-2019	1985	Panama	Bahamas, Belize, British Virgin Islands, Cyprus, Dubai, Geneva, Hong Kong, New Zealand, Panama, Seychelles, Singapore, Switzerland, Uruguay	https://icij.org/investigations/pandora-papers/
Alpha	Alpha Consulting Limited	823305	1996-2020	2008	Seychelles	Seychelles, United Arab Emirates, Belize	https://icij.org/investigations/pandora-papers/
Asiaciti	Asiaciti Trust Asia Limited	1800650	1996-2019	1978	Hong Kong	Singapore, Hong Kong, Cook Islands, Nevis, New Zealand, Panama, Samoa	https://icij.org/investigations/pandora-papers/
CCSL	CCS Trust Limited	149378	2001-2017	2005	Belize	Belize	https://icij.org/investigations/pandora-papers/
Cititrust	CIL Trust International	459476	1996-2019	1994	Belize	Belize, Seychelles	https://icij.org/investigations/pandora-papers/
Commence BVI	Commence Overseas Limited	8661	2004-2017	1992	British Virgin Islands	British Virgin Islands	https://icij.org/investigations/pandora-papers/
DAD Law	Demetrios A. Demetriades LLC	469184	1993-2021	1966	Cyprus	Cyprus	https://icij.org/investigations/pandora-papers/
Fidelity	Fidelity Corporate Services Limited	213733	1998-2019	2005	British Virgin Islands	British Virgin Islands, Seychelles, Belize, Latvia, Gibraltar, Hong Kong and Cyprus	https://icij.org/investigations/pandora-papers/
GDG	Glenn D. Godfrey and Company LLP	189907	1980-2019	2003	Belize	Belize	https://icij.org/investigations/pandora-papers/
Il Shin	Il Shin Corporate Consulting Limited	1575840	1996-2020	2004	Hong Kong	Hong Kong, Vietnam, China	https://icij.org/investigations/pandora-papers/
Il Shin	Il Shin CPA Limited	1575840	1996-2020	2007	Hong Kong	Hong Kong, Vietnam, China	https://icij.org/investigations/pandora-papers/
OMC	Overseas Management Company Inc	190477	1997-2020	1961	Panama	Anguilla, Bahamas, Belize, British Virgin Islands, Costa Rica, Hong Kong, New Zealand, Panama, Saint Kitts and Nevis, Seychelles, Switzerland, United Arab Emirates, Uruguay 	https://icij.org/investigations/pandora-papers/
SFM Corporate Services	SFM Corporate Services SA	191623	2000-2019	2006	Switzerland	Anguilla, Bahamas, Belize, British Virgin Islands, Canada, Cayman Islands, Cyprus, Delaware, Florida, Gibraltar, Hong Kong, Latvia, St Lucia, Malta, Marshall Islands, Netherlands, Nevis, Mauritius, Panama, Ras Al Khaimah, Samoa, Seychelles, Singapore, Switzerland, United Kingdom, Vanuatu, St Vincent	https://icij.org/investigations/pandora-papers/
SFM Corporate Services	SFM Corporate Services Limited	191623	2000-2019	2006	United Arab Emirates	Anguilla, Bahamas, Belize, British Virgin Islands, Canada, Cayman Islands, Cyprus, Delaware, Florida, Gibraltar, Hong Kong, Latvia, St Lucia, Malta, Marshall Islands, Netherlands, Nevis, Mauritius, Panama, Ras Al Khaimah, Samoa, Seychelles, Singapore, Switzerland, United Kingdom, Vanuatu, St Vincent	https://icij.org/investigations/pandora-papers/
Trident	Trident Trust Company Limited	3375331	1970-2019	1986	British Virgin Islands	Bahamas, Barbados, British Virgin Islands, Canada, Cayman Islands, Cyprus, Dubai, Guernsey, Hong Kong, Isle of Man, Jersey, Luxembourg, Malta, Mauritius, Nevis, New Zealand, Panama, Seychelles, Singapore, Switzerland, United Kingdom, United States, US Virgin Islands	https://icij.org/investigations/pandora-papers/
</tsv>
